import { Box, Chip, TextField, Tooltip, IconButton, Button, useTheme } from '@mui/material';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { Filter, X } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

import Header from '../../components/Header';
import CustomTable from '../../components/CustomTable';
import { greenColor } from '../../config/colors';
import { tokens } from '../../theme';
import { useTranslation } from '../../hooks/useTranslation';
import useDebounce from '../../hooks/useDebounce';
import { formatDateBR, formatMoney } from '../../utils';
import ContractActionsMenu from './components/ContractActionsMenu';
import ContractFilterModal from './components/ContractFilterModal';
import StatusChip from './components/StatusChip';
import { EMPTY_CONTRACT_FILTERS } from '../../constants/contractFilters';
import { buildFilterChips } from './helpers';
import api from '../../services/api';

const FILTER_MODAL_INITIAL = { open: false };

const Contratos = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);

  const [search, setSearch] = useState('');
  const [filterModal, setFilterModal] = useState(FILTER_MODAL_INITIAL);
  const [activeFilters, setActiveFilters] = useState(EMPTY_CONTRACT_FILTERS);
  const [contracts, setContracts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [filterOptions, setFilterOptions] = useState({ clients: [], companies: [], situations: [], statuses: [], financingTypes: [], models: [] });

  const debouncedSearch = useDebounce(search, 300);

  useEffect(() => {
    const EMPTY = { clients: [], companies: [], situations: [], statuses: [], financingTypes: [], models: [] };
    api.get('/v1/contracts/filters')
      .then(({ data }) => setFilterOptions(data?.data ?? data ?? EMPTY))
      .catch((err) => console.error('[contracts/filters]', err));
  }, []);

  useEffect(() => {
    const controller = new AbortController();

    const fetchContracts = async () => {
      setLoading(true);
      try {
        const params = {
          q:            debouncedSearch.trim() || undefined,
          client:       activeFilters.client?.name || undefined,
          status:       activeFilters.status       || undefined,
          situation:    activeFilters.situation     || undefined,
          companyId:    activeFilters.company       || undefined,
          financingType: activeFilters.financingType || undefined,
          model:        activeFilters.model         || undefined,
          dateStart:    activeFilters.dateStart     || undefined,
          dateEnd:      activeFilters.dateEnd       || undefined,
          pageSize:     100,
        };

        const { data } = await api.get('/v1/contracts', { params, signal: controller.signal });
        setContracts(data.data ?? []);
      } catch (err) {
        if (err?.name !== 'CanceledError' && err?.code !== 'ERR_CANCELED') {
          setContracts([]);
        }
      } finally {
        if (!controller.signal.aborted) {
          setLoading(false);
        }
      }
    };

    fetchContracts();

    return () => controller.abort();
  }, [debouncedSearch, activeFilters]);

  const filterChips = useMemo(
    () => buildFilterChips(activeFilters, t, filterOptions),
    [activeFilters, t, filterOptions]
  );

  const handleRemoveChip = useCallback((chipKey) => {
    setActiveFilters((prev) => {
      const next = { ...prev };
      if (chipKey === 'period') {
        next.dateStart = '';
        next.dateEnd = '';
      } else {
        next[chipKey] = chipKey === 'client' ? null : '';
      }
      return next;
    });
  }, []);

  const handleEdit = useCallback(
    (row) => navigate(`/app/contratos/${row.id}/edit`),
    [navigate]
  );

  const handleRowClick = useCallback(
    (row) => navigate(`/app/contratos/${row.id}/edit`),
    [navigate]
  );

  const columns = [
    {
      field: 'id',
      headerName: t('contracts.columns.id'),
      render: (value) => (
        <span style={{ color: greenColor, fontWeight: 600, fontSize: '13px' }}>
          #{value}
        </span>
      ),
    },
    {
      field: 'client',
      headerName: t('contracts.columns.client'),
      render: (value) => (
        <span style={{ fontWeight: 500, fontSize: '14px', textTransform: 'uppercase', color: '#000' }}>
          {value}
        </span>
      ),
    },
    {
      field: 'value',
      headerName: t('contracts.columns.value'),
      render: (value) => (
        <span style={{ fontSize: '14px', color: colors.grey[200] }}>
          {formatMoney(value)}
        </span>
      ),
    },
    {
      field: 'date',
      headerName: t('contracts.columns.date'),
      render: (value) => formatDateBR(value, false),
    },
    {
      field: 'status',
      headerName: t('contracts.columns.status'),
      render: (value) => <StatusChip status={value} />,
    },
    {
      field: '_actions',
      headerName: '',
      width: 48,
      render: (_, row) => (
        <ContractActionsMenu row={row} onEdit={handleEdit} />
      ),
    },
  ];

  const hasActiveFilters = filterChips.length > 0;

  return (
    <Box m="20px">
      <Header
        title={t('contracts.title')}
        subtitle={t('contracts.subtitle')}
      />

      {/* Action bar */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: hasActiveFilters ? 1.5 : 3 }}>
        <TextField
          placeholder={t('forms.searchByName')}
          size="small"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          sx={{ flex: 1, maxWidth: 340 }}
        />

        <Button
          variant="contained"
          onClick={() => setSearch('')}
          sx={{
            bgcolor: greenColor,
            color: '#000',
            '&:hover': { bgcolor: '#6ec13b' },
            fontWeight: 700,
          }}
        >
          {t('common.clear').toUpperCase()}
        </Button>

        <Tooltip title={t('common.filter')}>
          <IconButton
            onClick={() => setFilterModal({ open: true })}
            aria-label={t('common.filter')}
            sx={{
              bgcolor: hasActiveFilters ? '#e8f5e9' : greenColor,
              color: hasActiveFilters ? '#2E7D32' : '#000',
              borderRadius: '8px',
              border: hasActiveFilters ? '2px solid #2E7D32' : 'none',
              '&:hover': { bgcolor: '#6ec13b', color: '#000' },
            }}
          >
            <Filter size={18} />
          </IconButton>
        </Tooltip>

        <Box flex={1} />

        <Button
          variant="contained"
          onClick={() => navigate('/app/contratos/new')}
          sx={{
            bgcolor: greenColor,
            color: '#000',
            '&:hover': { bgcolor: '#6ec13b' },
            whiteSpace: 'nowrap',
            fontWeight: 700,
          }}
        >
          {t('contracts.newContract')}
        </Button>
      </Box>

      {/* Chips de filtros ativos */}
      {hasActiveFilters && (
        <Box
          sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 2 }}
          role="list"
          aria-label={t('contracts.filterModal.activeFilters')}
        >
          {filterChips.map((chip) => (
            <Chip
              key={chip.key}
              label={chip.label}
              role="listitem"
              onDelete={() => handleRemoveChip(chip.key)}
              deleteIcon={
                <X
                  size={14}
                  aria-label={`${t('contracts.filterModal.removeFilter')} ${chip.label}`}
                />
              }
              size="small"
              sx={{
                bgcolor: '#e8f5e9',
                color: '#2E7D32',
                fontWeight: 600,
                fontSize: '12px',
                borderRadius: '6px',
                border: '1px solid #c8e6c9',
                '& .MuiChip-deleteIcon': { color: '#2E7D32', '&:hover': { color: '#1b5e20' } },
              }}
            />
          ))}
        </Box>
      )}

      {/* Tabela */}
      <Box sx={{ backgroundColor: '#fff', borderRadius: '8px', px: '20px', pb: '4px' }}>
        <CustomTable
          columns={columns}
          rows={contracts}
          rowKey="id"
          emptyMessage={loading ? t('common.loading') : t('contracts.empty')}
          rowsPerPageOptions={[5, 8, 10, 25]}
          elevation={0}
          onRowClick={handleRowClick}
        />
      </Box>

      {/* Modal de filtros */}
      <ContractFilterModal
        open={filterModal.open}
        onClose={() => setFilterModal(FILTER_MODAL_INITIAL)}
        onApply={setActiveFilters}
        initialFilters={activeFilters}
        options={filterOptions}
      />
    </Box>
  );
};

export default Contratos;
